import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, money, won, errMsg, getToken, fmtKST, isPast, previewUrl, bonusPreviewUrl, FILL_RULES } from "@/lib/api";
import { LimitedTag, LIMITED_NOTE, LIMITED_UNIT_NOTE } from "@/components/LimitedTag";
import { StatusBadge, FillBar, EmptyState } from "@/components/Bits";
import ClaimDialog from "@/components/MultiClaimDialog";
import FancallPanel from "@/components/FancallPanel";
import { CollectSetDialog } from "@/components/admin/CollectSetDialog";
import { ProofOfPurchase } from "@/components/ProofOfPurchase";
import { StatusTimeline } from "@/components/StatusTimeline";

const CLAIM_TAGS = {
  gom: "border-amber-400/50 bg-amber-400/15 text-amber-200",
  fixed: "border-pink-500/40 bg-pink-500/10 text-pink-300",
  soft: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  open: "border-sky-500/50 bg-sky-500/15 text-sky-300",
};

const InfoRow = ({ label, value, testId, warn }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-zinc-400">{label}</span>
    <span data-testid={testId} className={`text-right font-medium ${warn ? "text-red-400" : ""}`}>{value}</span>
  </div>
);

const Slot = ({ slot, setNumber, setOpen, setDropped, setLocked, canClaim, isAdmin, isUnit, onClaim, onRemove }) => {
  const tid = `slot-set-${setNumber}-${slot.member.replace(/\s+/g, "-").toLowerCase()}`;
  if (!slot.claim_id && setLocked) {
    return (
      <div data-testid={tid} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm opacity-60">
        <span className="font-medium text-zinc-500 truncate">{slot.member}</span>
        <span className="text-[10px] font-bold tracking-widest text-zinc-500">LOCKED</span>
      </div>
    );
  }
  if (!slot.claim_id) {
    return (
      <button
        data-testid={tid}
        disabled={!canClaim}
        onClick={() => onClaim(slot.member, setNumber)}
        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors duration-200 disabled:opacity-50 ${setOpen ? "border-emerald-500/25 bg-emerald-500/5 enabled:hover:bg-emerald-500/15" : "border-zinc-800 bg-zinc-900 enabled:hover:bg-zinc-800"}`}
      >
        <span className="font-medium text-zinc-200 truncate">{slot.member}</span>
        <span className={`text-[10px] font-bold tracking-widest ${setOpen ? "text-emerald-400" : "text-amber-400/80"}`}>FREE</span>
      </button>
    );
  }
  return (
    <div data-testid={tid} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${setDropped ? "border-zinc-800 bg-zinc-900/40 opacity-60" : setOpen ? "border-zinc-800 bg-zinc-800/60" : "border-amber-500/25 bg-amber-500/5"}`}>
      <span className="font-medium text-zinc-400 truncate">{slot.member}</span>
      <span className="flex items-center gap-1 min-w-0">
        {slot.ot8 && <span className="text-[9px] font-bold tracking-wider text-pink-300">{isUnit ? "ALL 4" : "OT8"}</span>}
        {!isUnit && CLAIM_TAGS[slot.claim_type] && <span data-testid={`${slot.claim_type}-pick-${slot.claim_id}`} className={`rounded border px-1 text-[9px] font-bold tracking-wider uppercase ${CLAIM_TAGS[slot.claim_type]}`}>{slot.claim_type}</span>}
        <span className={`truncate text-xs font-semibold ${setDropped ? "text-zinc-500 line-through" : setOpen ? "text-violet-300" : "text-amber-300"}`}>{slot.joiner_name}</span>
        {isAdmin && (
          <button data-testid={`remove-claim-${slot.claim_id}`} onClick={() => onRemove(slot)} className="text-zinc-500 hover:text-red-400 transition-colors" aria-label="Remove claim">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
};

const CollectBar = ({ s, canCollect, canUndo, onCollect, onUndo }) => {
  if (s.collected) {
    return (
      <div data-testid={`set-collected-${s.set_number}`} className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-xs text-sky-200">
        <span><span className="font-bold tracking-wide">COLLECTING INITIALS</span> · pay by {fmtKST(s.collected_pay_by)}</span>
        {canUndo && (
          <button type="button" data-testid={`uncollect-set-btn-${s.set_number}`} onClick={() => onUndo(s.set_number)}
            className="ml-auto rounded-full border border-sky-400/40 px-2.5 py-0.5 text-[11px] font-semibold transition-colors hover:bg-sky-500/20">
            Undo collect
          </button>
        )}
      </div>
    );
  }
  if (!canCollect) return null;
  return (
    <button type="button" data-testid={`collect-set-btn-${s.set_number}`} onClick={() => onCollect(s.set_number)}
      className="mb-3 w-full rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-200 transition-colors hover:bg-sky-500/20">
      Collect
    </button>
  );
};

const SetCard = ({ s, onCollect, onUndo, limitedOpen, ...rest }) => (
  <div data-testid={`set-card-${s.set_number}`} className={`rounded-2xl border p-4 ${s.complete ? "border-amber-500/30 bg-amber-500/[0.03]" : "border-white/5 bg-zinc-900/60"}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-sm font-bold">SET #{s.set_number} <span data-testid={`set-price-${s.set_number}`} className={`ml-1 text-xs font-medium ${s.complete ? "text-emerald-400" : "text-zinc-400"}`}>{money(s.member_price)}/{rest.isUnit ? "unit" : "member"}</span></span>
      {s.complete ? (
        <span className="flex items-center gap-1 text-xs font-semibold text-amber-400"><Check className="h-3.5 w-3.5" /> Filled{!s.open && " · Pending"}</span>
      ) : (
        <span className="flex items-center gap-2">
          {s.dropped && <span data-testid={`set-dropped-${s.set_number}`} title={`Below ${rest.threshold}/${s.slots.length} when the drop closed`} className="text-[10px] font-bold tracking-widest text-zinc-500">DROPPED</span>}
          {!s.open && !s.dropped && !rest.settled && <span data-testid={`set-pending-${s.set_number}`} className="text-[10px] font-bold tracking-widest text-amber-400">PENDING</span>}
          {!s.open && !s.dropped && rest.settled && <span data-testid={`set-closed-${s.set_number}`} className="text-[10px] font-bold tracking-widest text-zinc-500">CLOSED</span>}
          {s.open && rest.isUnit && s.filled > 0 && <span data-testid={`set-needs-full-${s.set_number}`} className="text-[10px] font-bold tracking-widest text-amber-400">NEEDS 4/4</span>}
          <span className="font-mono text-xs text-zinc-500">{s.filled}/{s.slots.length}</span>
        </span>
      )}
    </div>
    <CollectBar s={s} canCollect={rest.isAdmin && limitedOpen && s.filled >= (rest.threshold || s.slots.length)} canUndo={rest.isAdmin} onCollect={onCollect} onUndo={onUndo} />
    <div className="grid gap-1.5">
      {s.slots.map((sl) => <Slot key={sl.member} slot={sl} setNumber={s.set_number} setOpen={s.open} setDropped={s.dropped} setLocked={s.collected} {...rest} />)}
    </div>
  </div>
);

const AlmostFull = ({ sets, canClaim, onClaim }) => {
  const near = sets.filter((s) => s.open && s.filled === s.slots.length - 1 && s.slots.length === 4);
  if (!near.length) return null;
  return (
    <div data-testid="unit-set-alerts" className="mt-8 space-y-2">
      {near.map((s) => {
        const free = s.slots.find((sl) => !sl.claim_id).member;
        return (
          <div key={s.set_number} data-testid={`unit-set-alert-${s.set_number}`} className="rise flex flex-wrap items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/[0.08] px-4 py-3">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" /></span>
            <span className="flex-1 min-w-0 text-sm"><span className="font-semibold text-amber-200">1 unit left to complete Set #{s.set_number}</span><span className="text-zinc-300">: {free} is still open</span></span>
            {canClaim && (
              <button type="button" data-testid={`unit-set-alert-claim-${s.set_number}`} onClick={() => onClaim(free, s.set_number)}
                className="rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-semibold text-zinc-950 transition-colors duration-200 hover:bg-amber-300">
                Claim {free}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function DropDetail() {
  const { id } = useParams();
  const [drop, setDrop] = useState(null);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [collecting, setCollecting] = useState(null);
  const isAdmin = !!getToken();
  const load = useCallback(() => {
    api.get(`/drops/${id}`).then((r) => setDrop(r.data)).catch(() => setError(true));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const removeClaim = async (slot) => {
    const msg = slot.ot8 ? `Remove ${slot.joiner_name}'s ${isUnit ? "All 4 units" : "OT8"} claim? This frees the whole set.` : `Remove ${slot.joiner_name}'s claim on ${slot.member}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/claims/${slot.claim_id}`);
      toast.success("Claim removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const undoCollect = async (n) => {
    if (!window.confirm(`Undo collect for Set #${n}? The set reopens, and its pay-by date and late fees are cleared. Payments already sent stay on file.`)) return;
    try {
      await api.post(`/drops/${id}/uncollect-set`, { set_number: n });
      toast.success(`Set #${n} reopened`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (error) return <EmptyState testId="drop-not-found" title="Drop not found" />;
  if (!drop) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const closedByTime = isPast(drop.claims_close);
  const open = drop.status === "open" && !closedByTime;
  const canClaim = open || (isAdmin && ["open", "closed", "collecting_initials"].includes(drop.status));
  const img = previewUrl(drop);
  const rule = FILL_RULES[drop.fill_rule];
  const isUnit = drop.fill_rule === "unit";
  const noun = isUnit ? "unit" : "member";

  return (
    <div className="rise">
      <Link to="/drops" data-testid="back-to-drops" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> All drops
      </Link>
      <div className="mt-4 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={drop.status === "open" && drop.claims_closed ? "closed" : drop.status} label={drop.status === "open" && drop.claims_closed ? "Claims closed" : undefined} testId="drop-status" />
            {drop.round_type && <span data-testid="drop-round-type" className="rounded-full bg-pink-500/15 text-pink-200 px-2.5 py-0.5 text-xs font-semibold">{drop.round_type}</span>}
            {drop.album_type && <span data-testid="drop-album-type" className="rounded-full bg-violet-500/15 text-violet-200 px-2.5 py-0.5 text-xs font-semibold">{drop.album_type}</span>}
            {drop.limited && <LimitedTag testId="drop-limited-tag" />}
          </div>
          <h1 data-testid="drop-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 break-words">{drop.ver_name ? `${drop.store} - ${drop.ver_name}` : drop.store || drop.title}</h1>
          {drop.limited && <p className="text-zinc-400 mt-3 max-w-2xl" data-testid="drop-limited-note"><span className="font-semibold text-red-200">Limited Drop:</span> {isUnit ? LIMITED_UNIT_NOTE : LIMITED_NOTE}</p>}
          {rule && <p className="text-zinc-400 mt-3 max-w-2xl" data-testid="drop-fill-rule"><span className="font-semibold text-zinc-200">{rule.label} fill rule:</span> {rule.desc}.{drop.limited ? "" : " Claims beyond the set that's currently filling are pending until it opens."}</p>}
          <StatusTimeline status={drop.status} />
          {img && <img src={img} alt="Set preview" data-testid="drop-preview" className="mt-6 w-full max-h-[420px] object-contain rounded-2xl border border-white/10 bg-zinc-900" />}
          {(drop.notes || drop.bonus_items) && (
            <div data-testid="drop-notes" className="mt-5 max-w-2xl rounded-2xl border border-violet-500/20 bg-violet-500/[0.05] p-4">
              <div className="text-[11px] uppercase tracking-widest text-violet-300 font-semibold mb-1.5">Additional Information</div>
              {drop.notes && <p className="text-sm text-zinc-200 whitespace-pre-line break-words">{drop.notes}</p>}
              {drop.bonus_items && (
                <div data-testid="drop-bonus-items" className={drop.notes ? "mt-3 pt-3 border-t border-violet-500/15" : ""}>
                  <div className="text-xs font-semibold text-amber-200 mb-1.5">Bonus Items</div>
                  <ul className="space-y-1 text-sm">
                    {[3, 8].filter((n) => drop[`bonus_claim${n}`]).map((n) => (
                      <li key={n} data-testid={`drop-bonus-claim${n}`} className="flex gap-2">
                        <span className="shrink-0 rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200">Claim {n}</span>
                        <span className="text-zinc-200 break-words min-w-0">{drop[`bonus_claim${n}`]}</span>
                      </li>
                    ))}
                  </ul>
                  {bonusPreviewUrl(drop) && <img src={bonusPreviewUrl(drop)} alt="Bonus items preview" data-testid="drop-bonus-preview" className="mt-3 w-full max-h-[320px] object-contain rounded-xl border border-white/10 bg-zinc-900" />}
                </div>
              )}
            </div>
          )}
          <ProofOfPurchase drop={drop} isAdmin={isAdmin} onChange={load} />
        </div>
        <div className="rounded-2xl border border-white/10 bg-zinc-900/80 p-5 space-y-4">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-zinc-400">{isUnit ? "Price per unit (set must be 4/4)" : "Price per member (full 8/8 set)"}</span>
            <span className="text-right">
              <span data-testid="drop-price" className="block font-mono text-2xl font-bold">{money(drop.price_per_member)}</span>
              {drop.price_won != null && <span data-testid="drop-price-won" className="block font-mono text-sm text-zinc-400">{won(drop.price_won)}</span>}
            </span>
          </div>
          {drop.unfilled_price > drop.price_per_member && <InfoRow label="Set not full" value={`${money(drop.unfilled_price)} / member`} testId="drop-unfilled-price" />}
          {drop.pay_to && <InfoRow label="Send payments to" value={drop.pay_to} testId="drop-pay-to" />}
          {drop.claims_close && <InfoRow label="Claims close" value={fmtKST(drop.claims_close)} testId="drop-claims-close" warn={closedByTime} />}
          {drop.payment_due && <InfoRow label="Payment due" value={fmtKST(drop.payment_due)} testId="drop-payment-due" />}
          {drop.threshold && <InfoRow label="Sets open" value={`${drop.opened_sets} of ${drop.num_sets}`} testId="drop-opened-sets" />}
          <FillBar filled={drop.filled_slots} total={drop.total_slots} testId="drop-fill-bar" />
          <Button data-testid="open-claim-dialog-button" disabled={!canClaim} onClick={() => setDialog({})} className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:opacity-90 text-white font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> {open ? `Claim a ${noun}` : canClaim ? `Add a claim (admin)` : closedByTime ? "Claims closed" : "Drop closed"}
          </Button>
        </div>
      </div>

      {drop.has_fancall && <div className="mt-8"><FancallPanel drop={drop} onChanged={load} /></div>}

      {isUnit && <AlmostFull sets={drop.sets} canClaim={canClaim} onClaim={(member, set) => setDialog({ member, set })} />}

      <div className="mt-10">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">Availability by {noun}</div>
        <div className="flex flex-wrap gap-2" data-testid="member-availability">
          {drop.members.map((m) => (
            <span key={m} className={`rounded-full border px-3 py-1 text-sm ${drop.member_open[m] ? "border-white/10 bg-zinc-900" : "border-zinc-800 text-zinc-600 line-through"}`}>
              {m} <span className="font-mono text-xs text-zinc-400 ml-1">{drop.member_open[m]} left</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-8" data-testid="sets-grid">
        {drop.sets.map((s) => (
          <SetCard key={s.set_number} s={s} threshold={drop.threshold} settled={drop.settled} canClaim={canClaim} isAdmin={isAdmin} isUnit={isUnit} onClaim={(member, set) => setDialog({ member, set })} onRemove={removeClaim}
            limitedOpen={!!drop.limited && drop.status === "open"} onCollect={setCollecting} onUndo={undoCollect} />
        ))}
      </div>
      <CollectSetDialog dropId={drop.id} setNumber={collecting} onClose={() => setCollecting(null)} onDone={() => { setCollecting(null); load(); }} />

      {drop.waitlist?.length > 0 && (
        <div className="mt-8 rounded-2xl border border-red-500/25 bg-red-500/[0.03] p-5" data-testid="drop-waitlist">
          <div className="font-display text-lg font-bold">Waitlist <span className="font-mono text-sm text-zinc-500">({drop.waitlist.length})</span></div>
          <div className="text-xs text-zinc-500 mt-0.5">{isUnit || drop.limited ? "Every set is full, so these claims wait in line (first come). They move in if a spot frees up." : "Pushed out of every set by higher-priority claims (OT8, then GoM, Fixed, Soft, Open; first come within each). They move back in if a spot frees up."}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            {drop.waitlist.map((w) => (
              <span key={w.claim_id} data-testid={`waitlist-${w.claim_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1 text-sm">
                <span className="font-medium">{w.member === "OT8" && isUnit ? "All 4 units" : w.member}</span><span className="text-zinc-500">·</span><span className="text-zinc-300">{w.joiner_name}</span>{w.claim_type && <span className={`rounded border px-1 text-[9px] font-bold tracking-wider uppercase ${CLAIM_TAGS[w.claim_type] || CLAIM_TAGS.open}`}>{w.claim_type}</span>}
                {isAdmin && (
                  <button data-testid={`remove-waitlist-${w.claim_id}`} onClick={() => removeClaim({ ...w, member: w.member })} className="text-zinc-500 hover:text-red-400 transition-colors" aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {dialog && <ClaimDialog drop={drop} initial={dialog} onClose={() => setDialog(null)} onClaimed={load} />}
    </div>
  );
}
